// components/Dashboard/BrandInfoEdit.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from '@googlemaps/js-api-loader';
import GooglePlacesAutocomplete from 'react-google-places-autocomplete';

import { useDispatch, useSelector } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import { useAlert } from 'context/AlertContext';
import { updateBrandProfile } from '../../../redux/actions/brands';

import { categories, toCategoryId } from 'utils/categories';
import CategorySelectorGrid from 'components/CategorySelectorGrid';
import {
  validateFiles,
  combinedUploadError,
  uploadErrorMessage,
  IMAGE_ACCEPT,
} from 'utils/uploadValidation';
import { isNameTakenError } from 'utils/nameTaken';
import { CameraIcon, XCircleIcon } from '@heroicons/react/24/outline';

const MAX_MEDIA = 10;
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const makeId = () => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getComp = (components, type) =>
  components?.find((c) => c.types?.includes(type));

const getAnyComp = (components, types) =>
  components?.find((c) => types.some((t) => c.types?.includes(t)));

const extractBrandCityCountry = (components = []) => {
  const city =
    getAnyComp(components, [
      'locality',
      'postal_town',
      'administrative_area_level_3',
      'administrative_area_level_2',
    ])?.long_name || '';

  const countryComp = getComp(components, 'country');
  const country = countryComp?.long_name || '';
  const countryCode = countryComp?.short_name || '';

  return { city, country, countryCode };
};

const BrandInfoEdit = ({ exitEditMode }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showAlert } = useAlert();

  const { brand } = useSelector((s) => s.brands);

  // ----- basic fields -----
  const [username, setUsername] = useState(brand?.username || '');
  const [country, setCountry] = useState(brand?.country || '');
  const [city, setCity] = useState(brand?.city || '');
  const [countryCode, setCountryCode] = useState(brand?.countryCode || '');
  const [cityPlaceId, setCityPlaceId] = useState(brand?.cityPlaceId || '');
  const [locationCoords, setLocationCoords] = useState(
    brand?.locationCoords || null
  );
  const [cityOption, setCityOption] = useState(null);
  const [website, setWebsite] = useState(brand?.website || '');
  const [description, setDescription] = useState(brand?.description || '');
  const [longDescription, setLongDescription] = useState(
    brand?.longDescription || ''
  );
  const [foundingYear, setFoundingYear] = useState(brand?.foundingYear || '');

  // ----- media (existing + new) -----
  const [existingMedia, setExistingMedia] = useState(
    Array.isArray(brand?.media) ? [...brand.media] : []
  );
  // each new media item: { id, file, preview }
  const [newMedia, setNewMedia] = useState([]);

  // track created preview URLs so we can revoke them on unmount
  const previewUrlsRef = useRef(new Set());
  useEffect(() => {
    const urls = previewUrlsRef.current;
    return () => {
      urls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {}
      });
      urls.clear();
    };
  }, []);

  const remainingSlots = Math.max(
    0,
    MAX_MEDIA - (existingMedia.length + newMedia.length)
  );

  useEffect(() => {
    setUsername(brand?.username || '');
    setCountry(brand?.country || '');
    setCity(brand?.city || '');
    setCountryCode(brand?.countryCode || '');
    setCityPlaceId(brand?.cityPlaceId || '');
    setLocationCoords(brand?.locationCoords || null);

    const label = brand?.city || '';
    const pid = brand?.cityPlaceId || '';
    setCityOption(
      label ? { label, value: { place_id: pid, description: label } } : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?.id]);

  const showUploadErrors = (uploadErrors) => {
    showAlert({
      type: 'warning',
      title: t('upload.error_title', { defaultValue: 'Upload issue' }),
      message: uploadErrors.join('\n'),
      primaryAction: { label: t('common.ok') },
    });
  };

  const handleMediaChange = (e) => {
    const input = e.target;
    const picked = Array.from(input.files || []);
    if (!picked.length) return;

    const existingBytes = newMedia.reduce(
      (sum, item) => sum + (item.file?.size || 0),
      0
    );
    const { accepted: files, errors: uploadErrors } = validateFiles(picked, {
      kind: 'image',
      existingBytes,
      t,
    });
    if (uploadErrors.length) showUploadErrors(uploadErrors);
    if (!files.length) {
      input.value = '';
      return;
    }

    const allowed = files.slice(0, remainingSlots);
    if (!allowed.length) {
      input.value = '';
      return;
    }

    const items = allowed.map((file) => {
      const id = makeId();
      const preview = URL.createObjectURL(file);
      previewUrlsRef.current.add(preview);
      return { id, file, preview };
    });

    setNewMedia((prev) => [...prev, ...items]);
    input.value = ''; // allow re-adding same files later
  };

  const removeExistingMedia = (url) => {
    setExistingMedia((prev) => prev.filter((u) => u !== url));
  };

  const removeNewMedia = (id) => {
    setNewMedia((prev) => {
      const item = prev.find((x) => x.id === id);
      if (item?.preview) {
        try {
          URL.revokeObjectURL(item.preview);
        } catch {}
        previewUrlsRef.current.delete(item.preview);
      }
      return prev.filter((x) => x.id !== id);
    });
  };

  // ----- categories -----
  const initialCats = Array.isArray(brand?.categories) ? brand.categories : [];
  // toCategoryId: legacy brands store ids as strings, and both the picker and
  // the taxonomy lookup in onSubmit match with ===.
  const [selMacros, setSelMacros] = useState([
    ...new Set(initialCats.map((c) => toCategoryId(c.macroCategory))),
  ]);
  const [selMicros, setSelMicros] = useState([
    ...new Set(
      initialCats
        .flatMap((c) => (Array.isArray(c.microCategory) ? c.microCategory : []))
        .map(toCategoryId)
    ),
  ]);

  // ----- keywords -----
  const [keywords, setKeywords] = useState(
    Array.isArray(brand?.keywords) ? brand.keywords : []
  );
  const [newKeyword, setNewKeyword] = useState('');
  const addKeyword = () => {
    const k = newKeyword.trim();
    if (k && !keywords.includes(k)) {
      setKeywords((prev) => [...prev, k]);
      setNewKeyword('');
    }
  };
  const removeKeyword = (i) =>
    setKeywords((prev) => prev.filter((_, idx) => idx !== i));

  // ----- socials -----
  const baseSocials = brand?.socials || {};
  const [facebook, setFacebook] = useState(baseSocials.facebook || '');
  const [instagram, setInstagram] = useState(baseSocials.instagram || '');
  const [tiktok, setTiktok] = useState(baseSocials.tiktok || '');
  const [customSocials, setCustomSocials] = useState(
    Array.isArray(baseSocials.others) ? baseSocials.others : []
  );

  const addCustomSocial = () =>
    setCustomSocials((prev) => [...prev, { label: '', url: '' }]);
  const removeCustomSocial = (i) =>
    setCustomSocials((prev) => prev.filter((_, idx) => idx !== i));
  const updateCustomSocial = (i, key, val) =>
    setCustomSocials((prev) =>
      prev.map((row, idx) => (idx === i ? { ...row, [key]: val } : row))
    );

  // location
  const [, setMapsReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const setReady = () => !cancelled && setMapsReady(true);

    if (window.google?.maps?.places) {
      setReady();
      return () => {
        cancelled = true;
      };
    }

    if (!API_KEY) {
      console.warn('Missing Google Maps API key — skipping Google loader.');
      setReady();
      return () => {
        cancelled = true;
      };
    }

    if (!window.__gmapsPromise) {
      const loader = new Loader({
        apiKey: API_KEY,
        libraries: ['places'],
        id: 'gmaps-places',
        language: 'en',
      });
      window.__gmapsPromise = loader.load();
    }

    window.__gmapsPromise.then(setReady).catch((err) => {
      console.error('Google Maps JS failed to load', err);
      setReady();
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const placesAvailable = !!window.google?.maps?.places;

  // ----- submit -----
  const [saving, setSaving] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();

    const combinedError = combinedUploadError(
      newMedia.map((item) => item.file),
      t
    );
    if (combinedError) {
      showUploadErrors([combinedError]);
      return;
    }

    if (!username.trim()) {
      showAlert({
        type: 'warning',
        title: t('alert_dialog.edit.update_error_title'),
        message: t('dashboard.name_required', 'Name cannot be empty'),
        primaryAction: { label: t('common.ok') },
      });
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('username', username.trim());
      fd.append('country', country);
      fd.append('city', city);
      fd.append('countryCode', countryCode || '');
      fd.append('cityPlaceId', cityPlaceId || '');
      fd.append(
        'locationCoords',
        locationCoords ? JSON.stringify(locationCoords) : 'null'
      );
      fd.append('website', website);
      fd.append('description', description);
      fd.append('longDescription', longDescription);
      if (foundingYear) fd.append('foundingYear', foundingYear);

      const catPayload = selMacros.map((m) => ({
        macroCategory: m,
        microCategory: selMicros.filter((mi) =>
          (categories.find((c) => c.id === m)?.micro || []).some(
            (x) => x.id === mi
          )
        ),
      }));
      fd.append('categories', JSON.stringify(catPayload));

      fd.append('keywords', JSON.stringify(keywords));

      const socials = {
        website,
        facebook,
        instagram,
        tiktok,
        others: customSocials.filter((c) => c.label && c.url),
      };
      fd.append('socials', JSON.stringify(socials));

      // Media merge semantics
      fd.append('keepMedia', JSON.stringify(existingMedia));
      const original = Array.isArray(brand?.media) ? brand.media : [];
      const removedMedia = original.filter((u) => !existingMedia.includes(u));
      fd.append('removedMedia', JSON.stringify(removedMedia));
      newMedia.forEach(({ file }) => fd.append('media', file));

      await dispatch(updateBrandProfile(brand.id, fd));
      showAlert({
        title: t('alert_dialog.edit.update_success_title'),
        message: t('alert_dialog.edit.update_success_message'),
        primaryAction: {
          label: t('alert_dialog.edit.update_success_primary_action'),
        },
      });
      exitEditMode();
    } catch (err) {
      console.error(err);
      const uploadMessage = uploadErrorMessage(err, t);
      showAlert({
        title: t('alert_dialog.edit.update_error_title'),
        message: isNameTakenError(err)
          ? t('dashboard.name_taken')
          : uploadMessage || t('alert_dialog.edit.update_error_message'),
        primaryAction: {
          label: t('alert_dialog.edit.update_error_primary_action'),
        },
      });
    } finally {
      setSaving(false);
    }
  };

  const onCityChange = async (place) => {
    // cleared
    if (!place) {
      setCityOption(null);
      setCity('');
      setCityPlaceId('');
      setLocationCoords(null);

      // keep the display fields consistent
      // you can choose to keep country/countryCode or clear them; I’d clear:
      setCountry('');
      setCountryCode('');
      return;
    }

    setCityOption(place);

    const placeId = place?.value?.place_id || '';
    setCityPlaceId(placeId);

    const fallbackLabel = place?.label || '';
    // if user selects a city, use it immediately
    setCity(fallbackLabel);

    if (!placeId || !API_KEY) return;

    try {
      const resp = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?place_id=${placeId}&key=${API_KEY}`
      );
      const data = await resp.json();
      const result = data?.results?.[0];

      const components = result?.address_components || [];
      const parsed = extractBrandCityCountry(components);

      // Use parsed values if present
      if (parsed.city) setCity(parsed.city);
      if (parsed.country) setCountry(parsed.country);
      if (parsed.countryCode)
        setCountryCode(String(parsed.countryCode).toUpperCase());

      const coords = result?.geometry?.location;
      const lat = typeof coords?.lat === 'number' ? coords.lat : null;
      const lng = typeof coords?.lng === 'number' ? coords.lng : null;

      setLocationCoords(lat != null && lng != null ? { lat, lng } : null);

      // Keep UI label nice
      setCityOption({ ...place, label: parsed.city || fallbackLabel });
    } catch (e) {
      console.error('Brand city geocode error:', e);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="w-full max-w-4xl mx-auto bg-white rounded-2xl shadow p-4 sm:p-6 md:p-8 space-y-6"
    >
      {/* Media Upload */}
      <div className="flex flex-col space-y-2">
        <input
          type="file"
          id="brand-media-upload"
          multiple
          accept={IMAGE_ACCEPT}
          className="sr-only"
          onChange={handleMediaChange}
        />
        <label
          htmlFor="brand-media-upload"
          className="flex items-center justify-center px-4 py-2 bg-yellow-500 text-white rounded-lg cursor-pointer hover:bg-yellow-600"
        >
          <CameraIcon className="w-6 h-6 mr-2" />
          {t('dashboard.upload_media')}
          <span className="ml-2 text-xs opacity-80">
            {existingMedia.length + newMedia.length}/{MAX_MEDIA}
          </span>
        </label>

        <div className="mt-4 flex space-x-2 overflow-x-auto">
          {/* Existing media (removable) */}
          {existingMedia.map((url, i) => (
            <div key={`existing-${i}`} className="relative flex-shrink-0">
              <img
                src={url}
                alt="Existing Media"
                className="w-24 h-24 object-cover rounded-md border"
              />
              <button
                type="button"
                onClick={() => removeExistingMedia(url)}
                className="absolute top-0 right-0 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                title={t('common.remove')}
              >
                <XCircleIcon className="w-6 h-6 text-red-500" />
              </button>
            </div>
          ))}

          {/* Newly added media (removable) */}
          {newMedia.map((img) => (
            <div key={img.id} className="relative flex-shrink-0">
              <img
                src={img.preview}
                alt="New Media"
                className="w-24 h-24 object-cover rounded-md border"
              />
              <button
                type="button"
                onClick={() => removeNewMedia(img.id)}
                className="absolute top-0 right-0 bg-white rounded-full p-1 shadow hover:bg-gray-100"
                title={t('common.remove')}
              >
                <XCircleIcon className="w-6 h-6 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Core fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-yellow-03 text-sm font-medium mb-1">
            {t('dashboard.brand_name', 'Brand name')}
          </label>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={100}
            className="border rounded px-3 py-2 text-sm w-full"
          />
        </div>
        <div>
          <label className="block text-yellow-03 text-sm font-medium mb-1">
            {t('dashboard.country')}
          </label>
          <input
            value={country}
            onChange={(e) => {
              setCountry(e.target.value);
              setCountryCode('');
            }}
            className="border rounded px-3 py-2 text-sm w-full"
          />
        </div>
        <div>
          <label className="block text-yellow-03 text-sm font-medium mb-1">
            {t('dashboard.city')}
          </label>
          {placesAvailable ? (
            <GooglePlacesAutocomplete
              autocompletionRequest={{
                types: ['(cities)'],
              }}
              selectProps={{
                value: cityOption,
                noOptionsMessage: () => t('homepage.no_options'),
                onChange: onCityChange,
                isClearable: true,
                placeholder: t('dashboard.city'),
                className: 'text-sm',
              }}
            />
          ) : (
            <input
              value={city}
              onChange={(e) => {
                setCity(e.target.value);
                // manual typing => clear geo linkage
                setCityPlaceId('');
                setLocationCoords(null);
              }}
              className="border rounded px-3 py-2 text-sm w-full"
            />
          )}
        </div>
        <div>
          <label className="block text-yellow-03 text-sm font-medium mb-1">
            {t('dashboard.founding_year')}
          </label>
          <input
            value={foundingYear}
            onChange={(e) => setFoundingYear(e.target.value)}
            className="border rounded px-3 py-2 text-sm w-full"
            maxLength={4}
          />
        </div>
        <div>
          <label className="block text-yellow-03 text-sm font-medium mb-1">
            Website
          </label>
          <input
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className="border rounded px-3 py-2 text-sm w-full"
          />
        </div>
      </div>

      {/* Short Description */}
      <div>
        <label className="block text-yellow-03 text-sm font-medium mb-1">
          {t('dashboard.short_description')}
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value.slice(0, 200))}
          rows={3}
          className="border rounded px-3 py-2 text-sm w-full"
        />
        <p className="text-xs text-gray-500 mt-1">{description.length}/200</p>
      </div>

      {/* Long Description */}
      <div>
        <label className="block text-yellow-03 text-sm font-medium mb-1">
          {t('dashboard.about_your_brand')}
        </label>
        <textarea
          value={longDescription}
          onChange={(e) => setLongDescription(e.target.value.slice(0, 10000))}
          rows={6}
          className="border rounded px-3 py-2 text-sm w-full"
        />
        <p className="text-xs text-gray-500 mt-1">
          {longDescription.length}/10000
        </p>
      </div>

      {/* Categories */}
      <div>
        <CategorySelectorGrid
          selectedMacros={selMacros}
          setSelectedMacros={setSelMacros}
          selectedMicros={selMicros}
          setSelectedMicros={setSelMicros}
        />
      </div>

      {/* Keywords */}
      <div>
        <h4 className="text-sm font-semibold mb-2">
          {t('dashboard.keywords')}
        </h4>
        <div className="flex flex-wrap gap-2 mb-2">
          {keywords.map((kw, i) => (
            <span
              key={i}
              className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs flex items-center"
            >
              {kw}
              <button
                type="button"
                onClick={() => removeKeyword(i)}
                className="ml-1 text-red-500"
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            placeholder={t('dashboard.add_keyword')}
            className="border rounded px-3 py-2 text-sm flex-1"
          />
          <button
            type="button"
            onClick={addKeyword}
            className="bg-yellow-500 text-white px-3 rounded"
          >
            {t('common.add')}
          </button>
        </div>
      </div>

      {/* Socials */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold">{t('dashboard.social_links')}</h4>
        {[
          { label: 'Facebook', state: facebook, set: setFacebook },
          { label: 'Instagram', state: instagram, set: setInstagram },
          { label: 'TikTok', state: tiktok, set: setTiktok },
        ].map(({ label, state, set }) => (
          <div key={label}>
            <label className="block text-yellow-03 text-sm font-medium mb-1">
              {label}
            </label>
            <input
              value={state}
              onChange={(e) => set(e.target.value)}
              placeholder={`${label} URL`}
              className="border rounded px-3 py-2 text-sm w-full"
            />
          </div>
        ))}

        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm font-medium">{t('Custom links')}</span>
            <button
              type="button"
              onClick={addCustomSocial}
              className="text-xs px-2 py-1 rounded border"
            >
              {t('Add link')}
            </button>
          </div>
          {customSocials.map((row, i) => (
            <div
              key={i}
              className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 mb-2"
            >
              <input
                value={row.label}
                onChange={(e) => updateCustomSocial(i, 'label', e.target.value)}
                placeholder="Label (TikTok, etc)"
                className="border rounded px-3 py-2 text-sm"
              />
              <input
                value={row.url}
                onChange={(e) => updateCustomSocial(i, 'url', e.target.value)}
                placeholder="https://…"
                className="border rounded px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => removeCustomSocial(i)}
                className="px-2 rounded border text-sm"
              >
                {t('common.remove')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save */}
      <div className="pt-4 flex gap-3">
        <button
          type="button"
          onClick={exitEditMode}
          className="bg-gray-400 text-white px-4 py-2 rounded"
        >
          {t('dashboard.cancel')}
        </button>
        <button
          type="submit"
          disabled={saving}
          className="bg-yellow-500 text-white px-4 py-2 rounded"
        >
          {saving ? (
            <span className="inline-flex items-center gap-2">
              <ClipLoader size={16} color="#fff" /> {t('dashboard.saving')}
            </span>
          ) : (
            t('dashboard.save_changes')
          )}
        </button>
      </div>
    </form>
  );
};

export default BrandInfoEdit;
